// gym/GymSettings.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper,  Button, TextField,
  Alert, Snackbar, CircularProgress, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, Divider
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Save, Refresh, CurrencyExchange, Settings as SettingsIcon,
  Info, Warning
} from '@mui/icons-material';
import api from '../services/auth';

const GymSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    primary_currency: 'HTG',
    exchange_rate_usd_to_htg: '130.00',
    registration_fee_htg: '0.00',
    registration_fee_usd: '0.00',
    require_registration_fee: true
  });

  const CURRENCY_CHOICES = [
    { value: 'HTG', label: 'Gourdes (HTG)' },
    { value: 'USD', label: 'Dollars US (USD)' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gym/settings/');
      console.log('Settings response:', response.data);
      
      // L'API peut retourner une liste ou un objet unique
      let settingsData;
      if (Array.isArray(response.data)) {
        settingsData = response.data[0]; // Prend le premier si c'est une liste
      } else if (response.data.results) {
        settingsData = response.data.results[0]; // Pagination
      } else {
        settingsData = response.data;
      }
      
      if (settingsData) {
        setSettings(settingsData);
        setFormData({
          primary_currency: settingsData.primary_currency || 'HTG',
          exchange_rate_usd_to_htg: settingsData.exchange_rate_usd_to_htg || '130.00',
          registration_fee_htg: settingsData.registration_fee_htg || '0.00',
          registration_fee_usd: settingsData.registration_fee_usd || '0.00',
          require_registration_fee: settingsData.require_registration_fee !== false
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Si pas de settings existants, on garde les valeurs par défaut
      setSettings(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Préparer les données pour l'API
      const apiData = {
        primary_currency: formData.primary_currency,
        exchange_rate_usd_to_htg: parseFloat(formData.exchange_rate_usd_to_htg),
        registration_fee_htg: parseFloat(formData.registration_fee_htg),
        registration_fee_usd: parseFloat(formData.registration_fee_usd),
        require_registration_fee: formData.require_registration_fee
      };

      console.log('Saving settings:', apiData);

      let response;
      if (settings) {
        // Mise à jour
        response = await api.put(`/gym/settings/${settings.id}/`, apiData);
      } else {
        // Création
        response = await api.post('/gym/settings/', apiData);
      }

      setSettings(response.data);
      setSnackbar({
        open: true,
        message: 'Configuration enregistrée avec succès',
        severity: 'success'
      });

      // Recharger les settings
      fetchSettings();

    } catch (error) {
      console.error('Error saving settings:', error);
      let errorMessage = 'Erreur lors de l\'enregistrement';
      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          const errors = Object.values(error.response.data).flat();
          errorMessage = errors.join(', ');
        }
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const calculateEquivalent = (amount, currency, isFromUSD = true) => {
    const amountNum = parseFloat(amount) || 0;
    const rate = parseFloat(formData.exchange_rate_usd_to_htg) || 130;
    
    if (currency === 'USD') {
      return isFromUSD ? amountNum * rate : amountNum / rate;
    } else {
      return isFromUSD ? amountNum / rate : amountNum * rate;
    }
  };

  const formatCurrency = (amount, currency = 'HTG') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <SettingsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Configuration du Gym</Typography>
            <Typography variant="body2" color="textSecondary">
              Configurez les paramètres généraux de votre salle de sport
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchSettings}
          disabled={saving}
        >
          Actualiser
        </Button>
      </Box>

      {/* Informations */}
      {settings && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Dernière mise à jour : {new Date(settings.updated_at).toLocaleString('fr-FR')}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Paramètres de devise */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <CurrencyExchange color="primary" />
                  <Typography variant="h6">Paramètres de devise</Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Devise principale</InputLabel>
                      <Select
                        value={formData.primary_currency}
                        label="Devise principale"
                        onChange={handleChange('primary_currency')}
                      >
                        {CURRENCY_CHOICES.map(currency => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Cette devise sera utilisée pour les rapports et l'affichage principal
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Taux de change (1 USD = X HTG)"
                      value={formData.exchange_rate_usd_to_htg}
                      onChange={handleChange('exchange_rate_usd_to_htg')}
                      type="number"
                      required
                      inputProps={{ min: 1, step: 0.01 }}
                      InputProps={{
                        startAdornment: <CurrencyExchange sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Taux actuel : 1 USD = {formatCurrency(formData.exchange_rate_usd_to_htg, 'HTG')}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Frais d'inscription */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <Info color="primary" />
                  <Typography variant="h6">Frais d'inscription</Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.require_registration_fee}
                      onChange={handleChange('require_registration_fee')}
                      color="primary"
                    />
                  }
                  label="Exiger des frais d'inscription pour les nouveaux clients"
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 2 }} />

                {formData.require_registration_fee && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Frais en HTG"
                        value={formData.registration_fee_htg}
                        onChange={handleChange('registration_fee_htg')}
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText={
                          formData.registration_fee_htg && formData.exchange_rate_usd_to_htg
                            ? `≈ ${calculateEquivalent(formData.registration_fee_htg, 'HTG', false).toFixed(2)} USD`
                            : ''
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Frais en USD"
                        value={formData.registration_fee_usd}
                        onChange={handleChange('registration_fee_usd')}
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText={
                          formData.registration_fee_usd && formData.exchange_rate_usd_to_htg
                            ? `≈ ${calculateEquivalent(formData.registration_fee_usd, 'USD', true).toFixed(2)} HTG`
                            : ''
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info">
                        Les clients pourront payer dans les deux devises. Le système convertira automatiquement en fonction du taux de change.
                      </Alert>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Résumé */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Résumé de la configuration</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Devise principale :</Typography>
                    <Typography variant="body1">
                      {CURRENCY_CHOICES.find(c => c.value === formData.primary_currency)?.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Taux de change :</Typography>
                    <Typography variant="body1">
                      1 USD = {formatCurrency(formData.exchange_rate_usd_to_htg, 'HTG')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Frais d'inscription :</Typography>
                    <Typography variant="body1">
                      {formData.require_registration_fee ? 'Obligatoire' : 'Non requis'}
                    </Typography>
                  </Grid>
                  {formData.require_registration_fee && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Montant HTG :</Typography>
                        <Typography variant="body1">
                          {formatCurrency(formData.registration_fee_htg, 'HTG')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Montant USD :</Typography>
                        <Typography variant="body1">
                          {formatCurrency(formData.registration_fee_usd, 'USD')}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Boutons d'action */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                disabled={saving}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymSettings;